import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphqlApi } from '../../../services/graphql';

export interface IdentityVerificationProps {
  /** Whether this user has already completed 본인확인. */
  isVerified: boolean;
  /** Called after a successful verification so the parent can refetch. */
  onVerified: (result: { identityName?: string; phoneNumber?: string }) => void;
}

/**
 * 휴대폰 본인확인 (NICE) trigger.
 *
 * Flow, and why it is shaped this way:
 *   1. `startIdentityVerification` mints a server-side request row and returns
 *      the one-time NICE 표준창 URL to open.
 *   2. We open a popup and navigate it there. The popup is opened BEFORE that
 *      request is awaited: browsers only allow `window.open` while a user
 *      gesture is still being handled, and opening it after the await gets it
 *      blocked. So it opens blank and is pointed at the URL once we have it.
 *   3. NICE redirects the popup to our API Gateway callback, which exchanges the
 *      result server-to-server and then redirects to `/identity/callback`. That
 *      page postMessages back here.
 *   4. We then call `completeIdentityVerification`, which is the only step that
 *      writes to the user record — and it re-checks ownership server-side.
 *
 * The requestId is held in a ref rather than state because the message handler
 * must see the current value without re-subscribing.
 */
const IdentityVerification: React.FC<IdentityVerificationProps> = ({
  isVerified,
  onVerified,
}) => {
  const { t } = useTranslation('profile');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justVerified, setJustVerified] = useState(false);
  const requestIdRef = useRef<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  // A verification is single-use server-side, so claiming twice would fail the
  // second time and replace a success with an error. The popup already guards
  // against duplicate messages, but the listener is made idempotent too: a
  // duplicate could also arrive from a re-opened popup or a stray message.
  const claimedRef = useRef<string | null>(null);

  const claim = useCallback(
    async (requestId: string) => {
      if (claimedRef.current === requestId) return;
      claimedRef.current = requestId;

      setBusy(true);
      setError(null);
      const res = await graphqlApi.completeIdentityVerification(requestId);
      setBusy(false);
      if (res.success && res.data) {
        setJustVerified(true);
        onVerified({
          identityName: res.data.identityName,
          phoneNumber: res.data.phoneNumber,
        });
      } else {
        setError(res.error || t('myProfile.identity.failed', '본인인증에 실패했습니다.'));
      }
    },
    [onVerified, t],
  );

  // Listen for the popup's result. Origin-checked: the callback page is served
  // from our own origin, so anything else is discarded.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'identity-verification-result') return;

      const { status, requestId, reason } = e.data;
      // Prefer our own requestId: the popup echoes one back, but trusting the
      // locally-held value avoids acting on a mismatched message.
      const id = requestIdRef.current || requestId;

      if (status === 'success' && id) {
        claim(id);
      } else if (status === 'unavailable') {
        setError(t('myProfile.identity.unavailable', '본인인증 서비스가 아직 준비되지 않았습니다.'));
      } else {
        setError(
          reason === 'already_used'
            ? t('myProfile.identity.alreadyUsed', '이미 사용된 인증입니다. 다시 시도해 주세요.')
            : t('myProfile.identity.failed', '본인인증에 실패했습니다.'),
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [claim, t]);

  const start = async () => {
    setBusy(true);
    setError(null);
    setJustVerified(false);
    // New attempt: allow the next requestId to be claimed.
    claimedRef.current = null;

    // Opened here, while the click is still being handled, so the browser does
    // not treat it as an unsolicited popup. It sits blank until we have the URL.
    const popup = window.open(
      '',
      'nice-identity',
      'width=480,height=812,scrollbars=yes,resizable=yes',
    );
    if (!popup) {
      setBusy(false);
      setError(t('myProfile.identity.popupBlocked', '팝업이 차단되었습니다. 팝업 차단을 해제해 주세요.'));
      return;
    }
    popupRef.current = popup;

    const res = await graphqlApi.startIdentityVerification();
    if (!res.success || !res.data) {
      // Leaving a blank popup open would look like a hang.
      popup.close();
      setBusy(false);
      setError(res.error || t('myProfile.identity.failed', '본인인증에 실패했습니다.'));
      return;
    }

    requestIdRef.current = res.data.requestId;

    // `replace` rather than assigning `href` so the blank page does not become a
    // history entry the user can go "back" to inside the popup.
    popup.location.replace(res.data.popupUrl);

    setBusy(false);
  };

  // Nothing to show once verified. NICE sets phoneVerified alongside
  // identityVerified, so PhoneVerification is already displaying an 인증완료 chip
  // next to the locked phone number — a second badge here just said the same
  // thing twice. `justVerified` still suppresses the button between a successful
  // claim and the parent's refetch, so it cannot be clicked again.
  if (isVerified || justVerified) return null;

  return (
    <div>
      <button
        type="button"
        onClick={start}
        disabled={busy}
        className="px-3.5 py-2 text-[13px] font-bold rounded-lg bg-[#0F1E3D] text-white transition hover:bg-[#1B2F5C] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy
          ? t('myProfile.identity.processing', '처리 중...')
          : t('myProfile.identity.verify', '휴대폰 본인인증')}
      </button>
      <p className="mt-1.5 text-xs text-[#6B7794]">
        {t(
          'myProfile.identity.help',
          '본인 명의의 휴대폰으로 실명을 확인합니다. 이름·생년월일·성별·휴대폰번호를 NICE평가정보로부터 제공받습니다.',
        )}
      </p>
      {error && <p className="mt-1.5 text-xs text-[#F25151]">{error}</p>}
    </div>
  );
};

export default IdentityVerification;
